import { useCallback, useState } from 'react';

import { SupervisedUserCircleOutlined } from '@mui/icons-material/';
import { Box, Typography, Stack, Tooltip } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { useHistory } from 'react-router';
import { useLocation } from 'react-router-dom';

import Button from 'domains/core/components/Button';
import PageLayout from 'domains/core/components/PageLayout';
import ErrorSnackbar from 'domains/core/components/Snackbars/ErrorSnackbar';
import TextField from 'domains/core/components/TextField';
import NestedFilterBuilder from 'domains/segments/components/NestedFilterBuilder';
import PreviewCount from 'domains/segments/pages/SegmentCreatePage/PreviewCount';
import { NestedSegmentDefinitionInput, SegmentDefinition } from 'domains/segments/types';
import useCreateSegment, { SegmentPostBody } from 'hooks/mutations/useCreateSegment';
import useSegmentPreviewCount from 'hooks/mutations/useSegmentPreviewCount';
import QueryKeys from 'hooks/queries/keys';
import useQueryParams from 'hooks/useQueryParams';
import { URLPaths } from 'models/enums';

type SegmentCreateFormInputs = {
    segmentName: string;
};

/**
 * Creates audiences by segmenting the contacts based on nested filters.
 * This is the nested/grouped version of the segment create page,
 * gated behind the 'audience-builder-nested' feature flag.
 *
 * The filter-building UI + state lives in the shared NestedFilterBuilder; this page owns the
 * audience name, preview/save actions, and navigation.
 *
 * @returns The React node created by this component.
 */
const NestedSegmentCreatePage = () => {
    const [definition, setDefinition] = useState<NestedSegmentDefinitionInput | null>(null);
    const [isBuilderValid, setIsBuilderValid] = useState(false);
    const [isAudiencePreviewed, setIsAudiencePreviewed] = useState(false);

    const {
        control,
        formState: { isValid },
        getValues,
    } = useForm<SegmentCreateFormInputs>({
        mode: 'onTouched',
        defaultValues: { segmentName: '' },
    });

    const handleBuilderChange = useCallback((nextDefinition: NestedSegmentDefinitionInput, valid: boolean) => {
        setDefinition(nextDefinition);
        setIsBuilderValid(valid);
        setIsAudiencePreviewed(false);
    }, []);

    const {
        mutate: getAudienceCount,
        isError: isAudienceCountError,
        isLoading: isAudienceCountLoading,
        error: audienceCountError,
        data: audienceCount,
    } = useSegmentPreviewCount();

    const history = useHistory();
    const queryClient = useQueryClient();
    const query = useQueryParams();
    const parentFolderId = Number(query.get('parentFolderId'));

    const onSuccess = () => {
        queryClient.invalidateQueries({
            // Updating the audiences to get the last segment added
            predicate: (query: any) => {
                const firstQueryKey: string = query.queryKey[0];
                return firstQueryKey === QueryKeys.SEGMENTS[0];
            },
        });
        const segmentsPath = parentFolderId ? `${URLPaths.SEGMENTS_FOLDER}/${parentFolderId}` : URLPaths.SEGMENTS;
        history.push(segmentsPath);
    };

    const { mutate: saveSegment, error: createSegmentError, isError: isCreateSegmentError } = useCreateSegment({
        onSuccess,
    });

    const previewAudienceCount = () => {
        if (!definition) return;
        getAudienceCount(definition);
        setIsAudiencePreviewed(true);
    };

    const saveAudience = () => {
        if (!definition) return;
        const body: SegmentPostBody = {
            name: getValues('segmentName'),
            nestingAllowed: true,
            segmentDefinition: definition,
            ...(parentFolderId ? { parentFolderId } : {}),
        };
        saveSegment(body);
    };

    // Copy / Duplicate audience flow — the source definition seeds the builder.
    const location = useLocation();
    const state = location?.state as { segmentDefinition: SegmentDefinition };
    const initialDefinition = state?.segmentDefinition ?? null;

    const isPreviewDisabled = !isBuilderValid || isAudienceCountLoading;
    const isSaveDisabled = !isBuilderValid || !isValid;

    return (
        <PageLayout
            header={
                <Tooltip
                    title="contacts grouped by a set of filter criteria so you can send targeted messages"
                    aria-label="contacts grouped by a set of filter criteria so you can send targeted messages"
                >
                    <Typography variant="h4">audiences</Typography>
                </Tooltip>
            }
            headerIcon={<SupervisedUserCircleOutlined fontSize="inherit" />}
            headerAction={
                <Stack direction="row" columnGap="1rem">
                    <Button
                        onClick={() => {
                            const segmentsPath = parentFolderId
                                ? `${URLPaths.SEGMENTS_FOLDER}/${parentFolderId}`
                                : URLPaths.SEGMENTS;
                            history.push(segmentsPath);
                        }}
                    >
                        Cancel
                    </Button>
                    <Tooltip title={isSaveDisabled ? 'Fill all the fields to enable saving' : ''}>
                        <span>
                            <Button
                                disabled={isSaveDisabled}
                                endIcon={<SupervisedUserCircleOutlined />}
                                onClick={saveAudience}
                                variant="contained"
                            >
                                Save
                            </Button>
                        </span>
                    </Tooltip>
                </Stack>
            }
        >
            <Stack pb={4}>
                {isCreateSegmentError && <ErrorSnackbar errorMessage={createSegmentError.message} />}
                <Stack direction="row" alignItems="center" columnGap={2}>
                    <Controller
                        name="segmentName"
                        control={control}
                        defaultValue=""
                        rules={{
                            required: 'This field is required',
                            minLength: { value: 3, message: 'name must have at least 3 characters' },
                            maxLength: { value: 255, message: 'name must be under 255 characters' },
                        }}
                        render={({ field: { onChange, value, onBlur }, fieldState: { error, invalid } }) => (
                            <TextField
                                required
                                label="Audience name"
                                value={value}
                                onChange={onChange}
                                onBlur={onBlur}
                                error={invalid}
                                helperText={invalid ? error.message : null}
                                width="24rem"
                            />
                        )}
                    />
                    <Tooltip
                        title="Preview the number of current contacts that meet the applied filters"
                        aria-label="preview the number of current contacts that meet the applied filters"
                    >
                        <Box>
                            <Button
                                disabled={isPreviewDisabled}
                                variant="outlined"
                                onClick={previewAudienceCount}
                                size="medium"
                            >
                                Preview
                            </Button>
                        </Box>
                    </Tooltip>
                    <Stack direction="row" justifyContent="flex-start">
                        <PreviewCount
                            isAudiencePreviewed={isAudiencePreviewed}
                            data={audienceCount}
                            error={audienceCountError}
                            isError={isAudienceCountError}
                            loading={isAudienceCountLoading}
                        />
                    </Stack>
                </Stack>
                <Stack mt={4} mb={2}>
                    <Typography variant="h6">create audience</Typography>
                    <Typography variant="body2" marginTop=".5rem">
                        Start by selecting the contact types you want to target. Your filters will be applied to these
                        contacts only.
                    </Typography>
                </Stack>

                <NestedFilterBuilder initialDefinition={initialDefinition} onChange={handleBuilderChange} />
            </Stack>
        </PageLayout>
    );
};

export default NestedSegmentCreatePage;
